// auth.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, BehaviorSubject, filter, take, switchMap, Observable } from 'rxjs';
import { SessionService } from './session.service';
import { AuthService } from './auth.service';

import { authBackendEndPointToken, backendEndPointToken, patientBackendEndPointToken } from '../../app.config';

// ✅ Subject to prevent multiple simultaneous refresh requests
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const backendUrl = inject(backendEndPointToken);
  const patientBackendUrl = inject(patientBackendEndPointToken);
  const authPatientBackendUrl = inject(authBackendEndPointToken);

  // Define allowed targets for token attachment
  const isBackendRequest = req.url.startsWith(backendUrl) ||
    req.url.startsWith(patientBackendUrl) ||
    req.url.startsWith(authPatientBackendUrl)
  // || req.url.includes('/fhir'); // Optional fuzzy match for safety

  // Skip authentication endpoints (never add token even if on backend)
  const isAuthEndpoint = req.url.includes('/auth/login') ||
    req.url.includes('/auth/register') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/forgot-password') ||
    req.url.includes(authBackendEndPointToken + '/api') ||
    req.url.includes('/auth/reset-password');

  // If it's NOT a backend request OR it's an auth endpoint, skip token attachment
  if (!isBackendRequest || isAuthEndpoint) {
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  // Add authentication headers for all other requests
  const session = sessionService.getSession();
  let authReq = req;

  if (session?.accessToken) {
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${session.accessToken}`
    };

    // ✅ Add active role header if it exists
    if (session.role) {
      headers['X-Active-Role'] = session.role;
      console.log('🔍 Interceptor sending role:', session.role);
    }

    authReq = req.clone({
      setHeaders: headers
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized (Token Expired or Invalid)
      if (error.status === 401 && !isAuthEndpoint) {
        console.warn('🔒 401 Unauthorized - attempting token refresh:', req.url);

        // ✅ Check if this is a token expiration error
        if (error.error?.message?.includes('expired') ||
          error.error?.message?.includes('Lifetime validation failed')) {

          return handleTokenExpired(authService, sessionService, router, req, next);
        }

        // If not a token expiration, just logout
        console.error('🔐 Invalid credentials - logging out');
        sessionService.clearSession();
        router.navigate(['/login']);
      }

      // Handle 403 Forbidden
      if (error.status === 403) {
        console.warn('🚫 403 Forbidden:', req.url);
      }

      return throwError(() => error);
    })
  );
};

/// <summary>
/// Handle expired token by attempting refresh
/// </summary>
function handleTokenExpired(
  authService: AuthService,
  sessionService: SessionService,
  router: Router,
  req: any,
  next: any
): Observable<HttpEvent<any>> {
  // ✅ If already refreshing, wait for the refresh to complete
  if (isRefreshing) {
    console.log('⏳ Token refresh already in progress, queuing request...');
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token: string | null) => {
        console.log('🔄 Using refreshed token for queued request');
        const clonedReq = req.clone({
          setHeaders: {
            'Authorization': `Bearer ${token}`
          }
        });
        return next(clonedReq) as Observable<HttpEvent<any>>;
      }),
      catchError((refreshError) => {
        console.error('❌ Refresh failed, logging out');
        sessionService.clearSession();
        router.navigate(['/login']);
        return throwError(() => refreshError);
      })
    );
  }

  // ✅ Start token refresh
  isRefreshing = true;
  console.log('🔄 Starting token refresh...');

  return authService.refreshToken().pipe(
    switchMap((response: any) => {
      isRefreshing = false;

      if (response?.accessToken) {
        console.log('✅ Token refreshed successfully');

        // ✅ Update session with new token
        const session = sessionService.getSession();
        if (session) {
          session.accessToken = response.accessToken;
          if (response.refreshToken) {
            session.refreshToken = response.refreshToken;
          }
          sessionService.setSession(session);
        }

        // ✅ Notify other queued requests
        refreshTokenSubject.next(response.accessToken);

        // ✅ Retry original request with new token
        const clonedReq = req.clone({
          setHeaders: {
            'Authorization': `Bearer ${response.accessToken}`
          }
        });
        return next(clonedReq) as Observable<HttpEvent<any>>;
      }

      throw new Error('No access token in refresh response');
    }),
    catchError((refreshError) => {
      isRefreshing = false;
      console.error('❌ Token refresh failed:', refreshError);

      // ✅ Log out user on refresh failure
      sessionService.clearSession();
      router.navigate(['/login']);

      refreshTokenSubject.next(null);
      return throwError(() => refreshError);
    })
  );
}
